import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { getPaths } from '../utils/paths';

export default function DecisionTree() {
  const { language } = useLanguage();
  const paths = getPaths(language);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      q: language === 'nl' ? 'Hoe competitief is jouw markt?' : 'How competitive is your market?',
      options: [
        { label: language === 'nl' ? 'Lokaal / niche' : 'Local / niche', value: 'low' },
        { label: language === 'nl' ? 'Regionaal / gemiddeld' : 'Regional / medium', value: 'medium' },
        { label: language === 'nl' ? 'Nationaal / zeer competitief' : 'National / highly competitive', value: 'high' },
      ],
      key: 'competition',
    },
    {
      q: language === 'nl' ? 'Heb je een in-house team (developers, schrijvers)?' : 'Do you have an in-house team (developers, writers)?',
      options: [
        { label: language === 'nl' ? 'Ja, we hebben capaciteit' : 'Yes, we have capacity', value: 'yes' },
        { label: language === 'nl' ? 'Deels, maar beperkt' : 'Partly, but limited', value: 'partial' },
        { label: language === 'nl' ? 'Nee, ik heb ontzorging nodig' : 'No, I need full support', value: 'no' },
      ],
      key: 'team',
    },
    {
      q: language === 'nl' ? 'Hoe snel wil je resultaat zien?' : 'How quickly do you want to see results?',
      options: [
        { label: language === 'nl' ? 'Rustig opbouwen' : 'Build steadily', value: 'slow' },
        { label: language === 'nl' ? 'Versneld groeien' : 'Accelerated growth', value: 'medium' },
        { label: language === 'nl' ? 'Zo snel mogelijk' : 'As fast as possible', value: 'fast' },
      ],
      key: 'speed',
    },
  ];

  const handleAnswer = (key, value) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    setStep(step + 1);
  };

  const getRecommendation = () => {
    const { competition, team, speed } = answers;
    let score = 0;
    if (competition === 'medium') score += 1;
    if (competition === 'high') score += 2;
    if (team === 'partial') score += 1;
    if (team === 'no') score += 2;
    if (speed === 'medium') score += 1;
    if (speed === 'fast') score += 2;

    if (score <= 2) return 'basic';
    if (score <= 4) return 'pro';
    return 'authority';
  };

  const packages = {
    basic: {
      name: 'Basic',
      hours: '8',
      price: '720',
      reason: language === 'nl'
        ? 'Jouw markt is minder competitief en je hebt interne capaciteit. Het Basic pakket biedt de volledige holistische aanpak in een rustig tempo.'
        : 'Your market is less competitive and you have internal capacity. The Basic package offers the full holistic approach at a steady pace.',
    },
    pro: {
      name: 'Pro',
      hours: '16',
      price: '1.368',
      reason: language === 'nl'
        ? 'Je markt vraagt om meer strategische capaciteit. Het Pro pakket geeft je versnelde groei met meer uren en intensievere begeleiding.'
        : 'Your market demands more strategic capacity. The Pro package gives you accelerated growth with more hours and more intensive guidance.',
    },
    authority: {
      name: 'Authority',
      hours: '20',
      price: '1.615',
      reason: language === 'nl'
        ? 'In een competitieve markt is maximale inzet nodig. Het Authority pakket biedt de hoogste intensiteit om op alle fronten tegelijk te bewegen.'
        : 'In a competitive market, maximum effort is needed. The Authority package offers the highest intensity to move on all fronts simultaneously.',
    },
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
  };

  const isResult = step >= questions.length;
  const rec = isResult ? packages[getRecommendation()] : null;

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="decision-tree">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3 text-center">
          {language === 'nl' ? 'Pakketwijzer' : 'Package Guide'}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-12 text-center">
          {language === 'nl' ? 'Welk pakket past bij jou?' : 'Which package fits you?'}
        </h2>

        {/* Progress */}
        <div className="flex gap-2 mb-10 justify-center">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i < step ? 'bg-[#4286F4] w-12' : i === step && !isResult ? 'bg-[#4286F4]/40 w-12' : 'bg-gray-200 w-12'
              }`}
            />
          ))}
        </div>

        {!isResult ? (
          <div className="text-center" data-testid={`decision-step-${step}`}>
            <p className="text-xs text-[#999] uppercase tracking-widest mb-2">
              {language === 'nl' ? `Vraag ${step + 1} van ${questions.length}` : `Question ${step + 1} of ${questions.length}`}
            </p>
            <h3 className="text-xl font-bold text-[#08080A] mb-8">
              {questions[step].q}
            </h3>
            <div className="flex flex-col gap-3 max-w-md mx-auto">
              {questions[step].options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(questions[step].key, opt.value)}
                  className="w-full text-left px-6 py-4 rounded-xl border-2 border-gray-100 hover:border-[#4286F4] hover:bg-[#4286F4]/5 transition-all text-[#333] font-medium"
                  data-testid={`decision-option-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center" data-testid="decision-result">
            <div className="inline-block bg-[#4286F4]/10 text-[#4286F4] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              {language === 'nl' ? 'Aanbeveling' : 'Recommendation'}
            </div>
            <h3 className="text-3xl font-bold text-[#08080A] mb-2">{rec.name}</h3>
            <p className="text-[#999] text-sm mb-6">
              {rec.hours} {language === 'nl' ? 'uur per maand' : 'hours per month'} &middot; &euro;{rec.price} / {language === 'nl' ? 'mnd' : 'mo'}
            </p>
            <p className="text-[#555] leading-relaxed mb-8 max-w-lg mx-auto">
              {rec.reason}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={paths.contact}>
                <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="decision-cta">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <button
                onClick={reset}
                className="text-[#555] font-medium px-6 py-3.5 rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
                data-testid="decision-restart"
              >
                <RotateCcw className="h-4 w-4" />
                {language === 'nl' ? 'Opnieuw' : 'Start over'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
